import React, { useState, useEffect, useCallback } from 'react';
import { 
  X, ChevronRight, ChevronLeft, PresentationIcon,
  Check, Loader2, Target, Users, Building2, UserCircle
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useSafeMode } from '../../contexts/SafeModeContext';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { BrandingPreview } from './BrandingPreview';

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 'basics', label: 'Basics', description: 'Name and audience' },
  { id: 'products', label: 'Product', description: 'Select a product' },
  { id: 'branding', label: 'Branding', description: 'Choose template' },
];

const VisualizationCreateWizard = ({ onClose, onSuccess }) => {
  const { token } = useAuth();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [brandingTemplates, setBrandingTemplates] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    target_audience: 'team',
    product_ids: [],
    branding_template_id: null,
  });
  
  // Fetch products
  const fetchProducts = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/products`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      }
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, []);
  
  // Fetch branding templates
  const fetchBrandingTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/visualizations/branding-templates`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setBrandingTemplates(data);
        
        // Auto-select default template
        const defaultTemplate = data.find(t => t.is_default);
        if (defaultTemplate) {
          setFormData(prev => ({ ...prev, branding_template_id: defaultTemplate.template_id }));
        }
      }
    } catch (error) {
      console.error('Error fetching branding templates:', error);
    }
  }, [token]);
  
  useEffect(() => {
    fetchProducts();
    fetchBrandingTemplates();
  }, [fetchProducts, fetchBrandingTemplates]);
  
  // Validation per step
  const validateStep = () => {
    switch (currentStep) {
      case 0: // Basics
        if (!formData.name.trim()) {
          toast.error('Please enter a name for your visualization');
          return false;
        }
        return true;
      case 1: // Products
        if (formData.product_ids.length === 0) {
          toast.error('Please select at least one product');
          return false;
        }
        return true;
      case 2: // Branding
        return true;
      default:
        return true;
    }
  };
  
  // Navigation
  const handleNext = () => {
    if (validateStep()) {
      if (currentStep < STEPS.length - 1) {
        setCurrentStep(prev => prev + 1);
      } else {
        handleCreate();
      }
    }
  };
  
  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };
  
  // Create visualization
  const handleCreate = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/api/visualizations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        const data = await response.json();
        toast.success('Visualization created successfully');
        onSuccess(data);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to create visualization');
      }
    } catch (error) {
      console.error('Error creating visualization:', error);
      toast.error('Failed to create visualization');
    } finally {
      setLoading(false);
    }
  };
  
  // Render step content
  const renderStepContent = () => {
    switch (currentStep) {
      case 0: // Basics
        return (
          <div className="space-y-6">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name">Visualization Name</Label>
              <Input
                id="name"
                placeholder="e.g., Q1 2025 Roadmap"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                className={cn(colors.bgSecondary, colors.borderPrimary)}
                data-testid="viz-name-input"
              />
            </div>
            
            {/* Target Audience */}
            <div className="space-y-2">
              <Label>Target Audience</Label>
              <p className={cn("text-xs mb-3", colors.textMuted)}>
                Who is this visualization for? This helps the AI tailor the content.
              </p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'board', label: 'Board', icon: Target, description: 'Executive-level strategic overview' },
                  { value: 'team', label: 'Team', icon: Users, description: 'Internal team progress tracking' },
                  { value: 'customer', label: 'Customer', icon: Building2, description: 'Customer-facing roadmap updates' },
                  { value: 'custom', label: 'Custom', icon: UserCircle, description: 'Custom audience with specific context' },
                ].map(option => {
                  const Icon = option.icon;
                  const isSelected = formData.target_audience === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, target_audience: option.value }))}
                      className={cn(
                        "p-4 rounded-lg border text-left transition-all",
                        isSelected
                          ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                          : cn(colors.borderPrimary, colors.bgSecondary, "hover:border-purple-500/50")
                      )}
                      data-testid={`audience-${option.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={cn(
                          "w-10 h-10 rounded-lg flex items-center justify-center",
                          isSelected 
                            ? "bg-purple-500 text-white"
                            : "bg-gray-100 dark:bg-gray-800"
                        )}>
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                          <p className={cn("font-medium text-sm", colors.textPrimary)}>
                            {option.label}
                          </p>
                          <p className={cn("text-xs", colors.textMuted)}>
                            {option.description}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        );
        
      case 1: // Product
        return (
          <div className="space-y-4">
            <div>
              <Label>Select a Product</Label>
              <p className={cn("text-xs mt-1 mb-4", colors.textMuted)}>
                Choose the product for this visualization. 
                The AI will pull initiatives, epics, and releases from this product.
              </p>
            </div>
            
            {loadingProducts ? (
              <div className="flex items-center justify-center h-40">
                <Loader2 className="w-6 h-6 animate-spin text-purple-600" />
              </div>
            ) : products.length === 0 ? (
              <div className={cn("p-6 rounded-lg text-center", colors.bgSecondary)}>
                <p className={cn("text-sm", colors.textMuted)}>
                  No products found. Create a product first to use visualizations.
                </p>
              </div>
            ) : (
              <Select value={formData.product_ids[0] || ''} onValueChange={(v) => setFormData(prev => ({ ...prev, product_ids: [v] }))}>
                <SelectTrigger className="w-full" data-testid="product-select">
                  <SelectValue placeholder="Select a product..." />
                </SelectTrigger>
                <SelectContent>
                  {products.map(product => (
                    <SelectItem key={product.product_id} value={product.product_id} data-testid={`product-${product.product_id}`}>
                      {product.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>
        );
        
      case 2: // Branding
        return (
          <div className="space-y-4">
            <div>
              <Label>Branding Template</Label>
              <p className={cn("text-xs mt-1 mb-4", colors.textMuted)}>
                Select a branding template. You can customize all settings after creation.
              </p>
            </div>
            
            {/* Default option */}
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, branding_template_id: null }))}
              className={cn(
                "w-full rounded-lg border text-left transition-all",
                !formData.branding_template_id
                  ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                  : cn(colors.borderPrimary, colors.bgSecondary, "hover:border-purple-500/50")
              )}
              data-testid="template-default"
            >
              <div className="p-3 flex items-center gap-3">
                <div className={cn(
                  "w-5 h-5 rounded-full border flex items-center justify-center",
                  !formData.branding_template_id ? "bg-purple-500 border-purple-500" : colors.borderPrimary
                )}>
                  {!formData.branding_template_id && <Check className="w-3 h-3 text-white" />}
                </div>
                <p className={cn("text-sm font-medium", colors.textPrimary)}>Default styling</p>
              </div>
              <div className="px-3 pb-3">
                <BrandingPreview branding={{}} />
              </div>
            </button>

            {/* Templates */}
            {brandingTemplates.map(template => {
              const isSelected = formData.branding_template_id === template.template_id;
              const branding = template.branding || {};
              
              return (
                <button
                  key={template.template_id}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, branding_template_id: template.template_id }))}
                  className={cn(
                    "w-full rounded-lg border text-left transition-all",
                    isSelected
                      ? "border-purple-500 bg-purple-50 dark:bg-purple-900/20"
                      : cn(colors.borderPrimary, colors.bgSecondary, "hover:border-purple-500/50")
                  )}
                  data-testid={`template-${template.template_id}`}
                >
                  <div className="p-3 flex items-center gap-3">
                    <div className={cn(
                      "w-5 h-5 rounded-full border flex items-center justify-center",
                      isSelected ? "bg-purple-500 border-purple-500" : colors.borderPrimary
                    )}>
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>
                    <div className="flex-1 min-w-0 flex items-center gap-2">
                      <p className={cn("font-medium text-sm", colors.textPrimary)}>{template.name}</p>
                      {template.is_default && (
                        <span className="text-xs px-1.5 py-0.5 rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
                          Default
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="px-3 pb-3">
                    <BrandingPreview branding={branding} />
                  </div>
                </button>
              );
            })}
          </div>
        );
        
      default:
        return null;
    }
  };
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className={cn(
        "relative w-full max-w-lg mx-4 rounded-xl shadow-2xl overflow-hidden",
        colors.bgPrimary
      )}>
        {/* Header */}
        <div className={cn("flex items-center justify-between p-4 border-b", colors.borderPrimary)}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <PresentationIcon className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className={cn("font-semibold", colors.textPrimary)}>
                Create Visualization
              </h2>
              <p className={cn("text-xs", colors.textMuted)}>
                Step {currentStep + 1} of {STEPS.length}: {STEPS[currentStep].label}
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
        
        {/* Progress */}
        <div className={cn("px-4 py-3 border-b", colors.borderPrimary, colors.bgSecondary)}>
          <div className="flex items-center gap-2">
            {STEPS.map((step, index) => (
              <React.Fragment key={step.id}>
                <div className="flex items-center gap-2">
                  <div className={cn(
                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium transition-all",
                    index < currentStep
                      ? "bg-purple-500 text-white"
                      : index === currentStep
                        ? "bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300"
                        : cn(colors.bgPrimary, colors.textMuted)
                  )}>
                    {index < currentStep ? <Check className="w-3 h-3" /> : index + 1}
                  </div>
                  <span className={cn(
                    "text-xs hidden sm:inline",
                    index === currentStep ? colors.textPrimary : colors.textMuted
                  )}>
                    {step.label}
                  </span>
                </div>
                {index < STEPS.length - 1 && (
                  <div className={cn(
                    "flex-1 h-0.5 rounded",
                    index < currentStep ? "bg-purple-500" : colors.bgSecondary
                  )} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
        
        {/* Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {renderStepContent()}
        </div>
        
        {/* Footer */}
        <div className={cn("flex items-center justify-between p-4 border-t", colors.borderPrimary)}>
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 0}
            className="gap-1"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={loading}
            className="gap-1"
            data-testid="wizard-next-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Creating...
              </>
            ) : currentStep === STEPS.length - 1 ? (
              <>
                Create Visualization
                <Check className="w-4 h-4" />
              </>
            ) : (
              <>
                Next
                <ChevronRight className="w-4 h-4" />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default VisualizationCreateWizard;
